/**
 * Attach this viewer to a room screen's Hyperbeam session and expose its video
 * as a three.js VideoTexture (works inside WebXR, unlike DOM iframes).
 *
 * The SDK hands us the cloud browser's video track (videoTrackCb); we play it
 * in a detached <video> and texture the screen with it. Audio is played by the
 * SDK itself. Input comes from the 3D screen: pointer events → sendEvent with
 * 0..1 coordinates.
 *
 * Cost: every connected viewer is one Hyperbeam participant ($0.007/min), so a
 * viewer only connects while `active` (RoomScreen: within ~15 m of the
 * screen, with hysteresis), and a tab that's been hidden for a minute
 * disconnects and reattaches when shown. While connected, the viewer is
 * metered via reportHyperbeamConnection (a heartbeat to the function).
 */
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import type { HyperbeamEmbed } from '@hyperbeam/web';
import { hyperbeamClients, reportHyperbeamConnection } from '../services/roomScreens';

export type HyperbeamStatus = 'idle' | 'connecting' | 'playing' | 'reconnecting' | 'ended' | 'error';

const HIDDEN_DISCONNECT_MS = 60_000;

export function useHyperbeamScreen(screenId: string, embedUrl: string | null, sessionId: string | null = null, active = true) {
  const [texture, setTexture] = useState<THREE.VideoTexture | null>(null);
  const [status, setStatus] = useState<HyperbeamStatus>('idle');
  const [warning, setWarning] = useState<string | null>(null);
  const [aspect, setAspect] = useState<number | null>(null);
  const hbRef = useRef<HyperbeamEmbed | null>(null);
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || !document.hidden);

  // Hidden for a minute → drop the connection (stops billing for this viewer).
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onVis = () => {
      if (timer) { clearTimeout(timer); timer = null; }
      if (document.hidden) timer = setTimeout(() => setVisible(false), HIDDEN_DISCONNECT_MS);
      else setVisible(true);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { document.removeEventListener('visibilitychange', onVis); if (timer) clearTimeout(timer); };
  }, []);

  useEffect(() => {
    if (!embedUrl || !visible || !active) { setStatus('idle'); return; }
    let disposed = false;
    let metered = false;
    let hb: HyperbeamEmbed | null = null;
    let tex: THREE.VideoTexture | null = null;
    // The SDK needs a container element even when we take the raw track.
    const container = document.createElement('div');
    container.setAttribute('aria-hidden', 'true');
    // Never let the hidden embed take focus: on Quest / phones a focused field
    // inside it pops the system keyboard, whose typing goes nowhere useful.
    // Typing reaches the page via sendEvent (desktop keys, VR 3D keyboard).
    container.inert = true;
    Object.assign(container.style, { position: 'fixed', left: '-10000px', top: '0', width: '16px', height: '9px', pointerEvents: 'none', opacity: '0' });
    document.body.appendChild(container);
    const video = document.createElement('video');
    video.muted = true; // audio comes through the SDK
    video.playsInline = true;
    video.autoplay = true;
    setStatus('connecting');
    setWarning(null);

    (async () => {
      try {
        const { default: Hyperbeam } = await import('@hyperbeam/web');
        if (disposed) return;
        hb = await Hyperbeam(container, embedUrl, {
          delegateKeyboard: false, // WASD must keep moving the avatar
          timeout: 10000,
          videoTrackCb: (track) => {
            if (disposed) return;
            video.srcObject = new MediaStream([track]);
            void video.play().catch(() => { /* muted autoplay is always allowed */ });
          },
          onConnectionStateChange: ({ state }) => {
            if (disposed) return;
            if (state === 'playing') {
              setStatus('playing');
              if (!metered && sessionId) { metered = true; reportHyperbeamConnection(screenId, sessionId); }
            }
            else if (state === 'failed') setStatus('error');
            else setStatus(state === 'reconnecting' ? 'reconnecting' : 'connecting');
          },
          onDisconnect: ({ type }) => {
            if (disposed) return;
            setStatus(type === 'kicked' || type === 'unauthorized' || type === 'unknown' ? 'error' : 'ended');
            // The function clears dead sessions on the next read.
            window.dispatchEvent(new CustomEvent('room-screen-ended', { detail: { screenId } }));
          },
          onCloseWarning: ({ deadline }) => {
            if (disposed) return;
            setWarning(deadline ? 'Screen turns off soon. Click it to keep watching.' : null);
          },
        });
        if (disposed) { hb.destroy(); return; }
        hbRef.current = hb;
        hyperbeamClients.set(screenId, hb);
        // Connected = one billed participant: start metering this viewer.
        if (!metered && sessionId) { metered = true; reportHyperbeamConnection(screenId, sessionId); }
      } catch (e: any) {
        if (disposed) return;
        console.warn('[RoomScreen] Hyperbeam connect failed', e?.name, e?.message);
        setStatus(e?.name === 'SessionTerminatedError' ? 'ended' : 'error');
        if (e?.name === 'SessionTerminatedError') window.dispatchEvent(new CustomEvent('room-screen-ended', { detail: { screenId } }));
      }
    })();

    const onFrame = () => {
      if (disposed || tex) return;
      if (!video.videoWidth) return;
      tex = new THREE.VideoTexture(video);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = false;
      setAspect(video.videoWidth / video.videoHeight);
      setTexture(tex);
    };
    video.addEventListener('loadeddata', onFrame);
    video.addEventListener('playing', onFrame);
    video.addEventListener('resize', () => { if (video.videoWidth) setAspect(video.videoWidth / video.videoHeight); });

    return () => {
      disposed = true;
      if (metered) reportHyperbeamConnection(screenId, null);
      if (hyperbeamClients.get(screenId) === hb) hyperbeamClients.delete(screenId);
      try { hb?.destroy(); } catch { /* already gone */ }
      hbRef.current = null;
      video.pause(); video.srcObject = null;
      tex?.dispose();
      setTexture(null);
      setAspect(null);
      container.remove();
    };
  }, [screenId, embedUrl, sessionId, visible, active]);

  return { texture, status, warning, aspect, hbRef };
}
